<template>
  <AppDialogContent
    v-model="dialogValue"
    :loading="formSubmitted"
    :persistent="formSubmitted"
    :disabled-submit="initFormLoading || initEntryConfirmForm.submitNotAllowed"
    custom-header
    @submit-form="submit"
  >
    <template #header>
      <div class="d-flex justify-space-between py-4">
        <div v-if="!!responseType">
          <div class="text-h5 font-weight-bold">
            {{ responseTexts.headline }}
          </div>
        </div>
        <div v-else style="align-self: center">
          <div class="text-h5 font-weight-bold mb-2">
            {{ $t("events:entryConfirmForm.heading", "Confirm entry") }}
          </div>
          <p>
            {{
              $t(
                "events:entryConfirmForm.explanation.text",
                "You are going to mark an entry as confirmed."
              )
            }}
          </p>
        </div>
        <div class="float-end">
          <v-btn
            icon
            variant="text"
            :disabled="formSubmitted"
            class="mx-0"
            data-test="closeDialog"
            :aria-label="$t('common:closeButton.alt')"
            @click="dialogValue = false"
          >
            <v-icon>$iconTimesCircle</v-icon>
          </v-btn>
        </div>
      </div>
    </template>

    <template #default>
      <div v-if="!!responseType">
        <EntryFeiResponse
          :response-type="responseType"
          :mutation-errors="mutationErrors"
        >
          <template #default>
            {{ responseTexts.main }}
          </template>
          <template #reason>
            <div v-if="responseTexts.reason">
              {{ responseTexts.reason }}<br />
            </div>
          </template>
          <template #additional-message>
            {{ responseTexts.additional }}
          </template>
        </EntryFeiResponse>
      </div>
      <div v-else-if="entryLoading">
        <v-progress-linear indeterminate />
      </div>
      <div v-else-if="entry">
        <AppShowEventDetails v-if="event" :event="event" show-logo />

        <br />

        <AppEntryRegistrationOverview v-bind="{ registration, competition }">
          <template #competitionCodeheader>
            {{
              $t("events:entryConfirmForm.competitions.label", "Competitions")
            }}
          </template>
          <template #athleteNamesHeader>
            {{ $t("events:entryConfirmForm.athleteNames.label", "Athlete") }}
          </template>
          <template #horseNamesHeader>
            {{ $t("events:entryConfirmForm.horseNames.label", "Horse") }}
          </template>
        </AppEntryRegistrationOverview>

        <br />

        <AppAlertNotice center>
          {{
            $t(
              "events:entryConfirmForm.alertNote.heading",
              "Please pay attention to validation:"
            )
          }}

          <div>
            {{
              $t(
                "events:entryConfirmForm.alertNote.generalAlert.text",
                "You are going to confirm selected entry. After entry is confirmed it will be considered as definite and will not be unregistered from the event, even if it is not paid by the deadline for online entries. "
              )
            }}
          </div>

          <div>
            <v-progress-linear
              v-if="initFormLoading"
              indeterminate
              class="my-5"
            />
            <ul
              v-if="initEntryConfirmForm.feedbackMessages.length > 0"
              class="mt-2"
            >
              <li
                v-for="(txt, i) in initEntryConfirmForm.feedbackMessages"
                :key="i"
              >
                {{ displayText(txt) }}
              </li>
            </ul>
          </div>
        </AppAlertNotice>
      </div>
    </template>

    <template #submit>
      {{
        $t(
          "events:entryConfirmForm.confirmSubstitution.button",
          "Confirm entry"
        )
      }}
    </template>

    <template v-if="!!responseType" #actions>
      <v-row dense style="justify-content: flex-end">
        <div class="mb-1 text-center text-md-right">
          <v-btn
            rounded
            variant="outlined"
            min-width="180px"
            data-test="dialogClose"
            @click="dialogValue = false"
          >
            {{ responseTexts.okBtn }}
          </v-btn>
        </div>
      </v-row>
    </template>
  </AppDialogContent>
</template>
<script>
import { defineComponent, ref, computed } from "vue";
import gql from "fraql";
import { apolloClient } from "@/plugins/apollo";
import {
  useDisplayText,
  DisplayTextFragments
} from "@/composables/display_text";
import AppDialogContent from "@elements/AppDialogContent.vue";
import AppShowEventDetails from "@AppShows/event/AppShowEventDetails.vue";
import { useEntryFeiActionsInjections } from "./compositions";
import AppAlertNotice from "@elements/AppAlertNotice/index.vue";
import EntryFeiResponse from "./EntryFeiResponse.vue";
import AppEntryRegistrationOverview from "@elements/AppEntryRegistrationOverview.vue";
import { useQuery } from "@vue/apollo-composable";
import { injectSnackbarError } from "@/composables/injections";

export default defineComponent({
  name: "EntryConfirm",
  components: {
    AppDialogContent,
    AppShowEventDetails,
    AppEntryRegistrationOverview,
    AppAlertNotice,
    EntryFeiResponse
  },
  setup() {
    const injections = useEntryFeiActionsInjections();
    const { snackbarServerError } = injectSnackbarError();

    const responseType = ref(null);
    const mutationErrors = ref([]);
    const value = ref(false);
    const formSubmitted = ref(false);
    const dialogValue = computed({
      get() {
        return value.value;
      },
      set(v) {
        if (v == true && !value.value) {
          responseType.value = null;
          mutationErrors.value = [];
        }

        value.value = v;
      }
    });

    const entryLoading = ref(false);
    const entry = ref(null);

    const registration = computed(() => {
      return entry.value?.registration;
    });
    const event = computed(() => {
      return entry.value?.registration?.event;
    });
    const competition = computed(() => {
      return entry.value?.competition;
    });
    async function openFeiActionForm({ entry: e }) {
      entryLoading.value = true;
      dialogValue.value = true;

      try {
        const { data } = await apolloClient.query({
          query: gql`
          query eventEntryConfirmFetch($id: ID!) {
            entry(id: $id) {
              id
              connectedWithFei
              allowedMutations
              registration {
                id
                athletes {
                  id
                  name
                  ${AppEntryRegistrationOverview.fragments.personFields}
                }
                horses {
                  id
                  name
                  ${AppEntryRegistrationOverview.fragments.horseFields}
                }
                eventId
                event {
                  id
                  ${AppShowEventDetails.fragments.event}
                }
              }
              competition {
                id
                code
                ${AppEntryRegistrationOverview.fragments.competitionFields}
              }
            }
          }
        `,
          variables: {
            id: e.id
          },
          fetchPolicy: "network-only"
        });
        entry.value = data.entry;
      } catch (error) {
        snackbarServerError(error, { level: "error" });
      } finally {
        entryLoading.value = false;
      }
    }

    const { displayText } = useDisplayText();

    const initEntryConfirmForm = ref({
      feedbackMessages: [],
      submitNotAllowed: false
    });

    const { onResult, loading: initFormLoading } = useQuery(
      gql`
        query initEntryConfirmForm($entryId: ID!) {
          initEntryConfirmForm(entryId: $entryId) {
            feedbackMessages {
              ${DisplayTextFragments.textTranslation}
            }
            submitNotAllowed
          }
        }
      `,
      () => ({
        entryId: entry.value.id
      }),
      () => ({
        enabled: dialogValue.value == true && !!entry.value?.id
      })
    );

    onResult(({ data, loading }) => {
      if (loading) return;
      initEntryConfirmForm.value = data.initEntryConfirmForm;
    });

    return {
      openFeiActionForm,
      registration,
      event,
      competition,
      entry,
      entryLoading,

      initFormLoading,
      displayText,
      initEntryConfirmForm,

      dialogValue,
      responseType,
      mutationErrors,
      formSubmitted,

      ...injections
    };
  },

  computed: {
    responseTexts() {
      switch (this.responseType) {
        case "error":
          return {
            headline: this.$t(
              "events:entryConfirmForm.failed.heading",
              "Mark entry as confirmed"
            ),
            main: this.$t(
              "events:entryConfirmForm.failed.mainMessage.text",
              "Confirmation of entry has been failed."
            ),
            reason: this.$t(
              "events:entryConfirmForm.failed.reason.text",
              "The failure was due the following reason:"
            ),
            additional: this.$t(
              "events:entryConfirmForm.failed.additionalMessage.text",
              "Please contact your National Federation, if you need help on this matter."
            ),
            okBtn: this.$t("events:entryConfirmForm.failed.ok.button", "OK")
          };
        default:
          return null;
      }
    }
  },
  methods: {
    async submit() {
      if (this.initEntryConfirmForm.submitNotAllowed) return;

      this.formSubmitted = true;

      try {
        const { errors } = await this.$apollo.mutate({
          mutation: gql`
            mutation entryConfirm($entryId: ID!) {
              entryConfirm(input: { entryId: $entryId }) {
                entry {
                  id
                  status
                  resolution
                  allowedMutations
                  registration {
                    id
                    entries {
                      id
                      status
                      resolution
                    }
                  }
                }
              }
            }
          `,
          variables: {
            entryId: this.entry.id
          }
        });

        if (errors?.length) {
          this.responseType = "error";
          this.mutationErrors = errors;
        } else {
          this.dialogValue = false;
        }
      } catch (error) {
        this.responseType = "error";
        if (error?.graphQLErrors) {
          this.mutationErrors = error.graphQLErrors;
        }
      } finally {
        this.formSubmitted = false;
      }
    }
  }
});
</script>
